/* Spam Settings tab — extracted from FormBuilder.jsx (A7a) */
import React from 'react';
import RecipientsEditor from '../RecipientsEditor';
import {
  SPAM_TEST_SCENARIOS,
  previewLiveBehavior,
  verdictLabel,
} from '../../utils/spamFirewallTest';

function resultClass(status) {
  if (status === 'spam') return 'fb-result fb-result-spam';
  if (status === 'suspicious') return 'fb-result fb-result-suspicious';
  return 'fb-result fb-result-clean';
}

function verdictPillClass(verdict) {
  if (verdict === 'spam') return 'fb-btn-danger-sm';
  if (verdict === 'suspicious') return 'fb-btn-warn';
  return 'inbox-pill-btn-primary';
}

export default function SpamSettingsTab({
  isMobile,
  formSettings,
  setFormSettings,
  workspaceSettings,
  setWorkspaceSettings,
  aiReplyEnabled,
  currentUser,
  showToast,
  testSpamSubmission,
  testingSpam,
  spamTestResult,
  customTestData,
  setCustomTestData,
  onOpenKnowledgeTab,
}) {
  const previewVerdict = spamTestResult?.verdict || spamTestResult?.resultStatus || 'clean';
  const settingsForPreview = {
    ...formSettings,
    autoReply: aiReplyEnabled,
  };
  const livePreview = previewLiveBehavior(
    settingsForPreview,
    workspaceSettings,
    previewVerdict
  );

  return (
        <div className={`fb-card${isMobile ? ' is-mobile' : ''}`}>
          <h2 className="fb-section-title">Firewall</h2>
          <p className="fb-muted" style={{ marginBottom: '2rem' }}>
            Configure how spam is detected and handled for this form.
          </p>
          
          <div className="fb-stack">
            <div className="fb-callout info">
              <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.6 }}>
                Business knowledge is managed in the{' '}
                <button
                  type="button"
                  className="today-link-accent fb-link-inline"
                  onClick={onOpenKnowledgeTab}
                >
                  Knowledge
                </button>
                {' '}tab. Scraping your site there fills both the knowledge base and the spam detection context automatically.
              </p>
            </div>

            {/* Spam Detection Toggles */}
            <div className="fb-divider">
              <h4 className="fb-subheading">Spam Protection</h4>
              <label className="fb-radio-label" style={{ marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formSettings.spamDetection}
                  onChange={(e) => setFormSettings({ ...formSettings, spamDetection: e.target.checked })}
                />
                <span style={{ fontSize: '0.9rem' }}>Enable Spam Detection</span>
              </label>
              <label className="fb-radio-label">
                <input
                  type="checkbox"
                  checked={formSettings.spamRedirect}
                  onChange={(e) => setFormSettings({ ...formSettings, spamRedirect: e.target.checked })}
                  disabled={!formSettings.spamDetection}
                />
                <span style={{ fontSize: '0.9rem', color: !formSettings.spamDetection ? 'var(--faint)' : 'var(--ink)' }}>
                  Redirect Spam to Other Spammers
                </span>
              </label>
            </div>

            {/* WHITELIST SECTION */}
            <div className="fb-callout success">
              <h4 className="fb-subheading">Whitelist (Override Spam Filter)</h4>
              <p className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                Add email domains or keywords to always bypass spam filtering. Useful for partners, vendors, or if spam detection is too aggressive.
              </p>
              
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  className="fb-input"
                  placeholder="@partnerdomain.com or keyword"
                  id="whitelist-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value) {
                      const newWhitelist = [...(formSettings.whitelist || []), e.target.value];
                      setFormSettings({ ...formSettings, whitelist: newWhitelist });
                      e.target.value = '';
                    }
                  }}
                  style={{ flex: 1, minWidth: 200 }}
                />
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  onClick={() => {
                    const input = document.getElementById('whitelist-input');
                    if (input && input.value) {
                      const newWhitelist = [...(formSettings.whitelist || []), input.value];
                      setFormSettings({ ...formSettings, whitelist: newWhitelist });
                      input.value = '';
                    }
                  }}
                >
                  Add
                </button>
              </div>
              
              {formSettings.whitelist && formSettings.whitelist.length > 0 && (
                <div className="fb-list-inner">
                  <div className="fb-label" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    Whitelisted ({formSettings.whitelist.length}):
                  </div>
                  {formSettings.whitelist.map((item, i) => (
                    <div key={i} className="fb-list-row">
                      <span style={{ fontSize: '0.85rem' }}>{item}</span>
                      <button
                        type="button"
                        className="fb-icon-remove"
                        onClick={() => {
                          const newWhitelist = formSettings.whitelist.filter((_, index) => index !== i);
                          setFormSettings({ ...formSettings, whitelist: newWhitelist });
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="fb-muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                <strong>Examples:</strong> @yourcompany.com (allows all emails from that domain), &quot;urgent&quot; (allows messages with that keyword), specific-email@example.com
              </p>
            </div>

            {/* BLOCK LIST SECTION */}
            <div className="fb-callout danger">
              <h4 className="fb-subheading">Block list (optional)</h4>
              <p className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                Block specific emails or domains. Exact matches only — future submissions from these senders are quarantined. Whitelist overrides block list.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  className="fb-input"
                  placeholder="name@example.com or @spammer.com"
                  id="blocklist-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value) {
                      const newBlockList = [...(formSettings.blockList || []), e.target.value];
                      setFormSettings({ ...formSettings, blockList: newBlockList });
                      e.target.value = '';
                    }
                  }}
                  style={{ flex: 1, minWidth: 200 }}
                />
                <button
                  type="button"
                  className="fb-btn-danger-sm"
                  style={{ padding: '0.75rem 1.25rem', fontSize: '0.9rem' }}
                  onClick={() => {
                    const input = document.getElementById('blocklist-input');
                    if (input && input.value) {
                      const newBlockList = [...(formSettings.blockList || []), input.value];
                      setFormSettings({ ...formSettings, blockList: newBlockList });
                      input.value = '';
                    }
                  }}
                >
                  Add
                </button>
              </div>
              {formSettings.blockList && formSettings.blockList.length > 0 && (
                <div className="fb-list-inner">
                  <div className="fb-label" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    Blocked ({formSettings.blockList.length}):
                  </div>
                  {formSettings.blockList.map((item, i) => (
                    <div key={i} className="fb-list-row">
                      <span style={{ fontSize: '0.85rem' }}>{item}</span>
                      <button
                        type="button"
                        className="fb-icon-remove"
                        onClick={() => {
                          const newBlockList = formSettings.blockList.filter((_, index) => index !== i);
                          setFormSettings({ ...formSettings, blockList: newBlockList });
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="fb-muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                <strong>Examples:</strong> spammer@example.com, @competitor.com
              </p>
            </div>

            {/* NOTIFICATION RECIPIENTS */}
            <div>
              <RecipientsEditor
                recipients={formSettings.recipients || []}
                ownerEmail={currentUser?.email || ''}
                onChange={(updated) => setFormSettings({ ...formSettings, recipients: updated })}
              />
              <p className="fb-muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                Saved with the <strong>Save Form</strong> button above.
              </p>
            </div>

            {/* HOW IT WORKS SECTION */}
            <div className="fb-divider-section" style={{ marginTop: '1rem' }}>
              <h3 className="fb-subsection-title" style={{ marginBottom: '1rem' }}>How Spam Protection Works</h3>
              
              <div className="fb-callout info" style={{ marginBottom: '2rem' }}>
                <h4 className="fb-subheading">Silent Neutralization</h4>
                <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                  When spam is detected (score 70+), we use a legal, effective approach:
                </p>
                <ul style={{ lineHeight: '1.8', color: 'var(--ink)', paddingLeft: '1.25rem' }}>
                  <li><strong>Bot thinks it succeeded</strong> - Receives &quot;Thank you for your submission&quot; message</li>
                  <li><strong>Data is silently discarded</strong> - Never reaches your CRM, email, or ad platforms</li>
                  <li><strong>15-second delay (Tarpitting)</strong> - Wastes bot&apos;s time and resources</li>
                  <li><strong>You never see it</strong> - Spam doesn&apos;t appear in your dashboard</li>
                  <li><strong>No notifications sent</strong> - Zero distraction from fake leads</li>
                </ul>
                
                <div className="fb-list-inner" style={{ marginTop: '1rem' }}>
                  <strong style={{ color: 'var(--danger)' }}>What This Costs Spammers:</strong>
                  <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.25rem' }}>
                    <li>15 seconds wasted per submission (timeout/delay)</li>
                    <li>~$0.15 per attempt (proxy + compute costs)</li>
                    <li>95% drop in campaign throughput</li>
                    <li>Operations become economically unviable</li>
                    <li>Eventually stop targeting your forms</li>
                  </ul>
                </div>
              </div>

              <div className="fb-callout tip" style={{ marginBottom: '2rem' }}>
                <h4 className="fb-subheading">What Makes This Legal and Effective</h4>
                <ul style={{ lineHeight: '1.8', marginBottom: 0, paddingLeft: '1.25rem' }}>
                  <li><strong>Pure defense</strong> - We don&apos;t contact spammers, just ignore them</li>
                  <li><strong>Slow response time</strong> - Completely legal to be slow</li>
                  <li><strong>No retaliation</strong> - Just protecting your systems</li>
                  <li><strong>Enterprise compliant</strong> - Passes legal review</li>
                  <li><strong>Platform approved</strong> - Works with Google Ads, Stripe, etc.</li>
                </ul>
              </div>
            </div>

            {/* SPAM REVIEW POLICY */}
            <div className="fb-divider-section">
              <h3 className="fb-subsection-title" style={{ marginBottom: '1rem' }}>Review &amp; repeat offenders</h3>
              <label className="fb-radio-label" style={{ marginBottom: '0.75rem', display: 'block' }}>
                <input
                  type="checkbox"
                  checked={formSettings.autoReplyToSuspicious === true}
                  onChange={(e) =>
                    setFormSettings({ ...formSettings, autoReplyToSuspicious: e.target.checked })
                  }
                />
                <span style={{ fontSize: '0.9rem' }}>
                  Auto-reply to suspicious leads (default: hold AI for your review)
                </span>
              </label>
              <label className="fb-radio-label" style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  checked={workspaceSettings?.autoBlockRepeatSpam === true}
                  onChange={(e) =>
                    setWorkspaceSettings({
                      ...workspaceSettings,
                      autoBlockRepeatSpam: e.target.checked,
                    })
                  }
                />
                <span style={{ fontSize: '0.9rem' }}>
                  Automatically block senders after 3 flagged submissions (you can always unblock)
                </span>
              </label>
              <p className="fb-muted" style={{ fontSize: '0.85rem', marginTop: '0.75rem', marginBottom: 0 }}>
                Saved with <strong>Save Form</strong>. Changing toggles updates the live-behavior preview below.
              </p>
            </div>

            {/* SPAM TESTING SECTION */}
            <div className="fb-divider-section">
              <h3 className="fb-subsection-title" style={{ marginBottom: '1rem' }}>Test Spam Detection</h3>
              <p className="fb-muted" style={{ marginBottom: '1rem' }}>
                Runs the same live code path as real submissions. Test leads appear in your inbox with a TEST chip — no owner emails or AI replies are sent.
              </p>

              <div className="fb-callout info" style={{ marginBottom: '1.5rem' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.6 }}>
                  {livePreview}
                  {!spamTestResult && (
                    <span className="fb-muted" style={{ display: 'block', marginTop: '0.5rem' }}>
                      Run a scenario below to see the verdict for your current settings.
                    </span>
                  )}
                </p>
              </div>

              <div
                className="fb-test-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: '0.75rem',
                  marginBottom: '1.5rem',
                }}
              >
                {SPAM_TEST_SCENARIOS.map((scenario) => (
                  <button
                    key={scenario.id}
                    type="button"
                    className={
                      scenario.tone === 'danger'
                        ? 'fb-btn-danger'
                        : scenario.tone === 'warn'
                          ? 'fb-btn-warn'
                          : 'inbox-pill-btn-primary'
                    }
                    onClick={() => testSpamSubmission(scenario.id)}
                    disabled={testingSpam}
                    style={{
                      padding: '1rem',
                      textAlign: 'left',
                      opacity: testingSpam ? 0.55 : 1,
                      cursor: testingSpam ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '0.35rem',
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{scenario.label}</span>
                    <span className="fb-muted" style={{ fontSize: '0.8rem', fontWeight: 400 }}>
                      {scenario.description}
                    </span>
                  </button>
                ))}
              </div>

              {spamTestResult && (
                <div className={resultClass(spamTestResult.resultStatus)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                    <span className={verdictPillClass(spamTestResult.resultStatus)} style={{ pointerEvents: 'none' }}>
                      {verdictLabel(spamTestResult.resultStatus)}
                    </span>
                    <span style={{ fontSize: '0.9rem' }}>
                      Score: <strong>{spamTestResult.data?.spamScore ?? '—'}</strong>
                    </span>
                  </div>
                  <p style={{ margin: '0 0 0.75rem', fontWeight: 600 }}>
                    What happens live: {spamTestResult.liveBehaviorSummary}
                  </p>
                  <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
                    {spamTestResult.testModeNote}
                  </p>
                  {spamTestResult.data?.reasons?.length > 0 && (
                    <div>
                      <strong>Reasons:</strong>
                      <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.25rem' }}>
                        {spamTestResult.data.reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {spamTestResult.resultStatus === 'spam' && (
                    <div className="fb-callout info" style={{ marginTop: '1rem', marginBottom: 0 }}>
                      <strong>Silent neutralization</strong>
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', lineHeight: 1.6 }}>
                        The bot sees a thank-you message. Data is discarded — nothing hits your inbox, CRM, or ad platforms.
                        High-confidence spam may also trigger a ~15s tarpit delay to waste bot resources.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Custom Test */}
              <div className="fb-callout info" style={{ marginTop: '2rem' }}>
                <h4 className="fb-subheading">Custom Test</h4>
                <p className="fb-muted" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Test with your own data to see how the spam filter handles it:
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <input
                    type="text"
                    className="fb-input"
                    placeholder="Name"
                    value={customTestData.name}
                    onChange={(e) => setCustomTestData({ ...customTestData, name: e.target.value })}
                  />
                  <input
                    type="email"
                    className="fb-input"
                    placeholder="Email"
                    value={customTestData.email}
                    onChange={(e) => setCustomTestData({ ...customTestData, email: e.target.value })}
                  />
                  <textarea
                    className="fb-textarea"
                    placeholder="Message (leave empty to test contact-only)"
                    rows={3}
                    value={customTestData.message}
                    onChange={(e) => setCustomTestData({ ...customTestData, message: e.target.value })}
                  />
                  <button
                    type="button"
                    className="inbox-pill-btn-primary"
                    onClick={() => testSpamSubmission('custom')}
                    disabled={testingSpam || !customTestData.email}
                    style={{ opacity: (testingSpam || !customTestData.email) ? 0.55 : 1, cursor: (testingSpam || !customTestData.email) ? 'not-allowed' : 'pointer' }}
                  >
                    {testingSpam ? 'Testing…' : 'Test Custom Submission'}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
  );
}
